import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, Heart, Users, Tag, Calendar, Trash2, Plus, Store, Pencil, X, KeyRound } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { MediaManager } from "@/components/MediaManager";
import { DescriptionEditor } from "@/components/DescriptionEditor";

const CATS = ["Food & Drink", "Home/Car Services", "Beauty", "Shopping", "Events", "Hidden Gems", "Health & Wellness", "Local Markets"];

export default function BusinessDashboard() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(undefined);
  const [stats, setStats] = useState(null);
  const [claimCode, setClaimCode] = useState("");
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && user.role !== "business_owner" && user.role !== "customer") navigate("/");
  }, [user, loading, navigate]);

  const load = async () => {
    const { data } = await api.get("/businesses/mine");
    setBiz(data);
    if (data) { const s = await api.get(`/businesses/${data.id}/stats`); setStats(s.data); }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const claim = async () => {
    if (!claimCode.trim()) return toast.error("Enter a claim code");
    setClaiming(true);
    try {
      await api.post("/businesses/claim", { code: claimCode.trim() });
      toast.success("Business claimed! It's now yours to manage.");
      setClaimCode("");
      await checkAuth();
      await load();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); } finally { setClaiming(false); }
  };

  if (!user || (user.role !== "business_owner" && user.role !== "customer")) return null;
  if (biz === undefined) return <div className="p-20 text-center text-slate-400">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 min-h-[70vh]">
      <h1 className="font-display text-4xl font-semibold tracking-tight">Business Dashboard</h1>
      {biz && <Badge className={`mt-2 rounded-full ${biz.status === "approved" ? "bg-green-100 text-green-700" : biz.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{biz.status}</Badge>}

      {!biz ? (
        <div className="mt-6 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 max-w-3xl" data-testid="claim-card">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><KeyRound className="w-5 h-5 text-orange-500" /> Have a claim code?</h3>
            <p className="text-sm text-slate-500 mt-1 mb-3">If Voyes set up a business page for you, enter the claim code you were given to take ownership and manage it.</p>
            <div className="flex flex-col sm:flex-row gap-2 max-w-md">
              <Input data-testid="claim-code-input" value={claimCode} onChange={(e) => setClaimCode(e.target.value.toUpperCase())} placeholder="e.g. A1B2C3D4" className="rounded-full uppercase tracking-widest" />
              <Button data-testid="claim-submit" onClick={claim} disabled={claiming} className="rounded-full bg-orange-500 hover:bg-orange-600">{claiming ? "Claiming…" : "Claim business"}</Button>
            </div>
          </div>
          {user.role === "business_owner" ? (
            <BusinessForm onSaved={load} />
          ) : (
            <p className="text-slate-400 text-sm max-w-3xl">Want to list your own business? Sign up with a business account to create one, or enter a claim code above.</p>
          )}
        </div>
      ) : (
        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="rounded-full bg-[#F3F0EA] p-1">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full">Overview</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-full">Edit Profile</TabsTrigger>
            <TabsTrigger value="deals" data-testid="tab-deals" className="rounded-full">Deals</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events" className="rounded-full">Events</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[["Views", stats?.views, Eye], ["Favorites", stats?.favorites, Heart], ["Followers", stats?.followers, Users], ["Deals", stats?.deals, Tag]].map(([l, v, Icon]) => (
                <div key={l} className="bg-white rounded-2xl border border-slate-100 p-5">
                  <Icon className="w-5 h-5 text-orange-500 mb-2" />
                  <p className="text-3xl font-display font-semibold">{v ?? 0}</p>
                  <p className="text-sm text-slate-500">{l}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-6 flex items-center gap-4">
              <img src={biz.logo} alt="" className="w-16 h-16 rounded-2xl object-cover" />
              <div className="flex-1">
                <h3 className="font-display text-xl font-semibold">{biz.name}</h3>
                <p className="text-sm text-slate-500">{biz.address}</p>
              </div>
              <Button variant="outline" className="rounded-full" onClick={() => navigate(`/business/${biz.id}`)}>View public page</Button>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <BusinessForm existing={biz} onSaved={load} />
          </TabsContent>

          <TabsContent value="deals" className="mt-6"><DealsManager biz={biz} onChange={load} /></TabsContent>
          <TabsContent value="events" className="mt-6"><EventsManager biz={biz} onChange={load} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export function BusinessForm({ existing, onSaved, createEndpoint = "/businesses", admin = false }) {
  const [f, setF] = useState(() => {
    if (existing) {
      const media = existing.media?.length ? existing.media : [existing.cover, ...(existing.gallery || [])].filter(Boolean);
      return { ...existing, media, featured: existing.featured || existing.cover || media[0] || "" };
    }
    return {
      name: "", description: "", categories: [], logo: "", cover: "", address: "", city: "Calgary",
      phone: "", website: "", instagram: "", gallery: [], media: [], featured: "", home_based: false, lat: 51.0447, lng: -114.0719, hours: {},
      font_family: "", font_size: 16, letter_spacing: 0, trending: false, hidden_gem: false,
    };
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleCat = (c) => set("categories", f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c]);

  const save = async () => {
    if (!f.name?.trim()) return toast.error("Business name required");
    setBusy(true);
    try {
      const media = Array.isArray(f.media) ? f.media : [];
      // keep cover in sync for cards/back-compat: use featured image, else first image
      const firstImg = (u) => u && !/\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(u);
      const coverPick = (f.featured && firstImg(f.featured)) ? f.featured : (media.find(firstImg) || f.cover || "");
      const payload = { ...f, media, featured: f.featured || (media[0] || ""), cover: coverPick,
        gallery: typeof f.gallery === "string" ? f.gallery.split(",").map((s) => s.trim()).filter(Boolean) : f.gallery };
      if (existing) await api.put(`/businesses/${existing.id}`, payload);
      else await api.post(createEndpoint, payload);
      toast.success(existing ? "Profile updated" : admin ? "Business added & published!" : "Business created! Pending admin approval.");
      onSaved();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 mt-6 space-y-5 max-w-3xl">
      {!existing && <div className="flex items-center gap-2 text-slate-500"><Store className="w-5 h-5" /> Create your business profile</div>}
      <div><Label>Business name</Label><Input data-testid="biz-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl mt-1" /></div>
      <DescriptionEditor testid="biz-description" value={f} onChange={(patch) => setF({ ...f, ...patch })} />
      <div>
        <Label>Categories</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {CATS.map((c) => (
            <button key={c} type="button" data-testid={`biz-cat-${c}`} onClick={() => toggleCat(c)}
              className={`px-3 py-1.5 rounded-full text-sm border-2 ${f.categories.includes(c) ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`}>{c}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ImageUpload label="Logo" testid="biz-logo" value={f.logo} onChange={(url) => set("logo", url)} previewClass="h-32" hint="Square logo works best" />
      </div>
      <MediaManager testid="biz-media" media={Array.isArray(f.media) ? f.media : []} featured={f.featured || ""} onChange={({ media, featured }) => setF({ ...f, media, featured })} />
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Address</Label><Input data-testid="biz-address" value={f.address} onChange={(e) => set("address", e.target.value)} className="rounded-xl mt-1" /></div>
        <div><Label>City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} className="rounded-xl mt-1" /></div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl mt-1" /></div>
        <div><Label>Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} className="rounded-xl mt-1" /></div>
        <div><Label>Instagram</Label><Input value={f.instagram} onChange={(e) => set("instagram", e.target.value)} className="rounded-xl mt-1" /></div>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="biz-home-based" checked={f.home_based} onChange={(e) => set("home_based", e.target.checked)} /> This is a home-based business</label>
      {admin && (
        <div className="bg-orange-50/60 border border-orange-100 rounded-2xl p-4 space-y-2">
          <p className="text-sm font-medium text-slate-700">Curation (admin only)</p>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="biz-trending" checked={!!f.trending} onChange={(e) => set("trending", e.target.checked)} /> Feature as Trending</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="biz-hidden-gem" checked={!!f.hidden_gem} onChange={(e) => set("hidden_gem", e.target.checked)} /> Mark as Hidden Gem</label>
        </div>
      )}
      <Button data-testid="biz-save" onClick={save} disabled={busy} className="rounded-full bg-orange-500 hover:bg-orange-600 px-8">{busy ? "Saving…" : existing ? "Save changes" : "Create business"}</Button>
    </div>
  );
}

function DealsManager({ biz, onChange }) {
  const [f, setF] = useState({ title: "", description: "", discount: "", valid_until: "" });
  const [editId, setEditId] = useState(null);
  const reset = () => { setF({ title: "", description: "", discount: "", valid_until: "" }); setEditId(null); };
  const save = async () => {
    if (!f.title) return toast.error("Title required");
    if (editId) {
      await api.put(`/deals/${editId}`, f);
      toast.success("Deal updated");
    } else {
      await api.post(`/businesses/${biz.id}/deals`, f);
      toast.success("Deal added");
    }
    reset(); onChange();
  };
  const startEdit = (d) => { setEditId(d.id); setF({ title: d.title || "", description: d.description || "", discount: d.discount || "", valid_until: d.valid_until || "" }); };
  const del = async (id) => { await api.delete(`/deals/${id}`); toast.success("Deal removed"); if (editId === id) reset(); onChange(); };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-3 self-start">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">{editId ? <><Pencil className="w-5 h-5" /> Edit deal</> : <><Plus className="w-5 h-5" /> New deal</>}</h3>
        <Input data-testid="deal-title" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl" />
        <Input data-testid="deal-discount" placeholder="Discount e.g. 20% OFF" value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} className="rounded-xl" />
        <Textarea data-testid="deal-desc" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" />
        <Input data-testid="deal-valid" type="date" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} className="rounded-xl" />
        <div className="flex gap-2">
          <Button data-testid="deal-add" onClick={save} className="rounded-full bg-orange-500 hover:bg-orange-600 flex-1">{editId ? "Save changes" : "Add deal"}</Button>
          {editId && <Button variant="outline" onClick={reset} className="rounded-full" data-testid="deal-cancel">Cancel</Button>}
        </div>
      </div>
      <div className="space-y-3">
        {biz.deals?.length ? biz.deals.map((d) => (
          <div key={d.id} className={`bg-white rounded-2xl border p-4 flex justify-between items-start ${editId === d.id ? "border-orange-300 ring-1 ring-orange-200" : "border-slate-100"}`}>
            <div><div className="flex items-center gap-2"><h4 className="font-semibold">{d.title}</h4>{d.discount && <Badge className="bg-orange-500 text-white rounded-full">{d.discount}</Badge>}</div><p className="text-sm text-slate-500">{d.description}</p></div>
            <div className="flex items-center gap-1 shrink-0">
              <button data-testid={`edit-deal-${d.id}`} onClick={() => startEdit(d)} className="text-slate-400 hover:text-orange-500 p-1"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`del-deal-${d.id}`} onClick={() => del(d.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        )) : <p className="text-slate-400 text-sm">No deals yet.</p>}
      </div>
    </div>
  );
}

function EventsManager({ biz, onChange }) {
  const empty = { title: "", description: "", date: "", time: "", location: "", price: 0, image: "", website: "", gallery: [], font_family: "", font_size: 16, letter_spacing: 0 };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const reset = () => { setF(empty); setEditId(null); };
  const save = async () => {
    if (!f.title) return toast.error("Title required");
    const payload = { ...f, price: Number(f.price) || 0 };
    if (editId) {
      await api.put(`/events/${editId}`, payload);
      toast.success("Event updated");
    } else {
      await api.post(`/businesses/${biz.id}/events`, payload);
      toast.success("Event added");
    }
    reset(); onChange();
  };
  const startEdit = (e) => { setEditId(e.id); setF({ ...empty, ...e }); window.scrollTo({ top: 200, behavior: "smooth" }); };
  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Event removed"); if (editId === id) reset(); onChange(); };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-3 self-start">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">{editId ? <><Pencil className="w-5 h-5" /> Edit event</> : <><Plus className="w-5 h-5" /> New event</>}</h3>
        <Input data-testid="event-title" placeholder="Event title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl" />
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="event-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="rounded-xl" />
          <Input data-testid="event-time" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className="rounded-xl" />
        </div>
        <Input data-testid="event-location" placeholder="Location / venue" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="rounded-xl" />
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="event-price" type="number" placeholder="Price (0 = free)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="rounded-xl" />
          <Input data-testid="event-website" placeholder="Website / ticket link" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="rounded-xl" />
        </div>
        <ImageUpload label="Event cover image" testid="event-image" value={f.image} onChange={(url) => setF({ ...f, image: url })} previewClass="h-32" />
        <ImageUpload label="Event photos (gallery)" testid="event-gallery" multiple value={f.gallery} onChange={(arr) => setF({ ...f, gallery: arr })} hint="Add more photos for the event page" />
        <DescriptionEditor testid="event-desc" value={f} onChange={(patch) => setF({ ...f, ...patch })} />
        <div className="flex gap-2">
          <Button data-testid="event-add" onClick={save} className="rounded-full bg-orange-500 hover:bg-orange-600 flex-1">{editId ? "Save changes" : "Add event"}</Button>
          {editId && <Button variant="outline" onClick={reset} className="rounded-full" data-testid="event-cancel">Cancel</Button>}
        </div>
      </div>
      <div className="space-y-3">
        {biz.events?.length ? biz.events.map((e) => (
          <div key={e.id} className={`bg-white rounded-2xl border p-4 flex justify-between items-start ${editId === e.id ? "border-orange-300 ring-1 ring-orange-200" : "border-slate-100"}`}>
            <div><div className="text-sm text-orange-600 font-medium flex items-center gap-1"><Calendar className="w-4 h-4" /> {e.date}{e.time ? ` · ${e.time}` : ""} · {e.price === 0 ? "Free" : `$${e.price}`}</div><h4 className="font-semibold">{e.title}</h4></div>
            <div className="flex items-center gap-1 shrink-0">
              <button data-testid={`edit-event-${e.id}`} onClick={() => startEdit(e)} className="text-slate-400 hover:text-orange-500 p-1"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`del-event-${e.id}`} onClick={() => del(e.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        )) : <p className="text-slate-400 text-sm">No events yet.</p>}
      </div>
    </div>
  );
}
